import type { MessageAction, RequestAction } from "trystero";
import {
  bindOnlineRoomActions,
  createOnlineOrder,
  handleOnlineControl,
  onlineErrorMessage,
  onlinePeerCounts,
  publishOnlineLobby,
  publishOnlineState,
  requestOnlineControl,
} from "./online-room-actions.js";
import { OnlineRoomChat } from "./online-room-chat.js";
import { OnlineRoomHostLoop } from "./online-room-host-loop.js";
import { OnlineRoomRecovery } from "./online-room-recovery.js";
import { requestRoomPacket } from "./online-room-request.js";
import { encodeOnlineRoomSave } from "./online-room-save.js";
import type {
  ControlReply,
  ControlRequest,
  OnlineRoomSessionOptions,
  OnlineRoomSnapshot,
} from "./online-room-types.js";
import { recoveryDescriptor } from "./recovery-descriptor.js";
import {
  localRoomHello,
  OnlineRoomAuthority,
  type RoomHello,
  type RoomOrder,
  type RoomOrderAck,
} from "./room-protocol.js";
import { TrysteroRoomTransport } from "./trystero-room.js";

export type { OnlineRoomSessionOptions, OnlineRoomSnapshot } from "./online-room-types.js";
export class OnlineRoomSession {
  readonly #transport: TrysteroRoomTransport;
  readonly #hello: RoomHello;
  readonly #listeners = new Set<(snapshot: OnlineRoomSnapshot) => void>();
  readonly #lobbyAction: MessageAction<string>;
  readonly #controlAction: RequestAction<string, string>;
  readonly #orderAction: RequestAction<string, string>;
  readonly #stateAction: MessageAction<string>;
  readonly #chat: OnlineRoomChat;
  #authority: OnlineRoomAuthority | undefined;
  readonly #recovery: OnlineRoomRecovery;
  readonly #hostLoop: OnlineRoomHostLoop;
  readonly #tickMs: number;
  #hostPeerId: string | undefined;
  #sequence = 0;
  #snapshot: OnlineRoomSnapshot;

  constructor(options: OnlineRoomSessionOptions) {
    const hello = localRoomHello(options.role, options.sessionId, options.reconnectToken);
    this.#hello = options.testOnlyContentDigest
      ? { ...hello, contentDigest: options.testOnlyContentDigest }
      : hello;
    this.#tickMs = options.tickMs ?? 250;
    if (!Number.isSafeInteger(this.#tickMs) || this.#tickMs < 1)
      throw new RangeError("Invalid online room tick interval");
    this.#transport = new TrysteroRoomTransport({
      roomId: options.roomId,
      hello: this.#hello,
      ...(options.relayUrl ? { relayUrl: options.relayUrl } : {}),
      ...(options.handshakeTimeoutMs ? { handshakeTimeoutMs: options.handshakeTimeoutMs } : {}),
    });
    this.#snapshot = {
      phase: options.role === "host" ? "lobby" : "connecting",
      roomId: options.roomId,
      role: options.role,
      seatId: options.role === "host" ? (options.config?.seats[0]?.id ?? null) : null,
      ...(options.config ? { config: structuredClone(options.config) } : {}),
      roster: [],
      chat: [],
      peerCount: 0,
      directConnections: 0,
    };
    if (options.role === "host") {
      if (!options.config) throw new TypeError("A room host must provide match configuration");
      this.#authority = options.resume
        ? OnlineRoomAuthority.resume(
            options.config,
            this.#transport.selfId,
            this.#hello,
            options.resume.certified.checkpoint,
          )
        : new OnlineRoomAuthority(options.config, this.#transport.selfId, this.#hello);
      this.update({ roster: this.#authority.roster() });
    }

    this.#lobbyAction = this.#transport.action("lobby");
    this.#controlAction = this.#transport.requestAction("control");
    this.#orderAction = this.#transport.requestAction("order");
    this.#stateAction = this.#transport.action("state");
    this.#chat = new OnlineRoomChat(
      this.#transport,
      this.#hello,
      () => this.#authority,
      () => this.#hostPeerId,
      () => this.#snapshot.chat,
      (chat) => this.update({ chat }),
    );
    this.#recovery = new OnlineRoomRecovery(this.#transport, this.#hello, {
      config: () => this.#snapshot.config,
      authority: () => this.#authority,
      installAuthority: (authority) => this.installRecoveredAuthority(authority),
      selectRemoteHost: (peerId) => this.selectRecoveredRemoteHost(peerId),
      update: (recovery, playing) =>
        this.update({ recovery, phase: playing ? "playing" : "recovering" }),
    });
    this.#hostLoop = new OnlineRoomHostLoop(
      this.#tickMs,
      () => this.#authority,
      this.#recovery,
      () => this.publishState(),
      (error) => this.fail(error),
    );
    this.bindActions();
    this.#transport.setEvents({
      peerJoined: (peerId, hello) => this.peerJoined(peerId, hello),
      peerLeft: (peerId) => this.peerLeft(peerId),
      joinFailed: (error) =>
        this.#authority ? this.update({ error: error.message }) : this.fail(error),
    });
  }

  getSnapshot(): OnlineRoomSnapshot {
    return this.#snapshot;
  }
  subscribe(listener: (snapshot: OnlineRoomSnapshot) => void): () => void {
    this.#listeners.add(listener);
    return () => this.#listeners.delete(listener);
  }
  async setReady(ready: boolean): Promise<void> {
    await this.control({ kind: "ready", ready }, async (authority) => {
      authority.setReady(this.#transport.selfId, ready);
      await this.publishLobby();
    });
  }

  async start(): Promise<void> {
    await this.control({ kind: "start" }, () => this.startAuthority(this.#transport.selfId));
  }

  async requestPause(paused: boolean): Promise<void> {
    await this.control({ kind: "request-pause", paused }, (authority) =>
      authority.requestPause(this.#transport.selfId, paused),
    );
  }

  async approvePause(): Promise<void> {
    await this.control({ kind: "approve-pause" }, (authority) =>
      authority.approvePause(this.#transport.selfId),
    );
  }

  async handoff(nextHostMemberId: string): Promise<void> {
    await this.#recovery.handoff(nextHostMemberId);
  }

  exportSave(): string {
    const config = this.#snapshot.config;
    if (!config) throw new TypeError("Room configuration is unavailable");
    return encodeOnlineRoomSave(config, this.#recovery.latestCertified());
  }

  availableSave(): string | undefined {
    const config = this.#snapshot.config;
    const certified = this.#recovery.availableCertified();
    return config && certified ? encodeOnlineRoomSave(config, certified) : undefined;
  }

  async submit(
    order: Pick<RoomOrder, "requestId" | "expectedScopeGeneration" | "intent">,
  ): Promise<RoomOrderAck> {
    if (!this.#recovery.acceptingOrders())
      return Object.freeze({
        ok: false,
        requestId: order.requestId,
        reason: "Order admission is frozen until a recovery majority is authoritative",
      });
    const nextSequence = Math.max(
      this.#sequence + 1,
      this.#snapshot.state?.nextControllerSequence ?? 1,
    );
    this.#sequence = nextSequence;
    const packet = createOnlineOrder(
      this.#snapshot,
      this.#recovery.descriptor()?.authorityEpoch ?? 1,
      nextSequence,
      order,
    );
    if (this.#authority) return this.#authority.submit(this.#transport.selfId, packet);
    const host = this.requiredHost();
    return requestRoomPacket<RoomOrderAck>(this.#orderAction, packet, host);
  }

  async sendChat(text: string): Promise<void> {
    await this.#chat.send(text, this.#transport.selfId);
  }

  peerConnections(): Readonly<Record<string, RTCPeerConnection>> {
    return this.#transport.peerConnections();
  }

  async dispose(): Promise<void> {
    this.#hostLoop.stop();
    this.#listeners.clear();
    this.#recovery.dispose();
    await this.#transport.leave();
  }

  private bindActions(): void {
    bindOnlineRoomActions({
      lobby: this.#lobbyAction,
      control: this.#controlAction,
      order: this.#orderAction,
      state: this.#stateAction,
      sessionId: this.#hello.sessionId,
      hostPeerId: () => this.#hostPeerId,
      authority: () => this.#authority,
      recovery: this.#recovery,
      handleControl: (raw, peerId) => this.handleControl(raw, peerId),
      update: (change) => this.update(change),
    });
  }

  private peerJoined(peerId: string, hello: RoomHello): void {
    this.#recovery.peerJoined(peerId, hello);
    if (this.#authority) {
      try {
        this.#authority.join(peerId, hello);
        void this.publishLobby(peerId);
        if (this.#authority.started) void this.publishState(peerId);
      } catch (error) {
        this.update({ error: onlineErrorMessage(error) });
      }
    } else if (this.#recovery.isCurrentHost(hello.sessionId)) {
      if (this.#hostPeerId && this.#hostPeerId !== peerId) {
        this.fail(new Error("Multiple host peers were discovered"));
        return;
      }
      this.#hostPeerId = peerId;
      void requestOnlineControl(this.#controlAction, { kind: "sync" }, peerId).catch((error) =>
        this.fail(error),
      );
    }
    this.update(onlinePeerCounts(this.#transport.peerConnections()));
  }

  private peerLeft(peerId: string): void {
    this.#recovery.peerLeft(peerId);
    if (this.#authority) {
      this.#authority.leave(peerId);
      void this.publishLobby();
    } else if (peerId === this.#hostPeerId) {
      this.#hostPeerId = undefined;
      if (this.#snapshot.recovery)
        this.update({
          recovery: {
            ...this.#snapshot.recovery,
            message: "Host disconnected; waiting for the heartbeat recovery threshold.",
          },
        });
    }
    this.update(onlinePeerCounts(this.#transport.peerConnections()));
  }

  private async handleControl(raw: string, peerId: string): Promise<ControlReply> {
    return handleOnlineControl(
      raw,
      peerId,
      this.#authority,
      (requester) => this.startAuthority(requester),
      () => this.publishLobby(),
    );
  }

  private startAuthority(peerId: string): void {
    const authority = this.#authority;
    if (!authority) throw new TypeError("Only the host runs room authority");
    authority.start(peerId);
    this.#recovery.initialize(recoveryDescriptor(authority.roster(), authority.authorityEpoch));
    this.update({ phase: "playing" });
    void this.publishLobby();
    void this.publishState();
    this.#hostLoop.start();
  }

  private async publishLobby(target?: string): Promise<void> {
    const authority = this.#authority;
    const config = this.#snapshot.config;
    if (!authority || !config) return;
    const roster = await publishOnlineLobby(
      this.#lobbyAction,
      authority,
      config,
      this.#recovery.descriptor(),
      target,
    );
    this.update({ roster });
  }
  private async publishState(target?: string): Promise<void> {
    const authority = this.#authority;
    if (!authority) return;
    const local = await publishOnlineState(
      this.#stateAction,
      authority,
      this.#transport.selfId,
      Object.keys(this.#transport.peerConnections()),
      target,
    );
    this.update({ phase: "playing", state: local, seatId: local.seatId });
  }

  private installRecoveredAuthority(authority: OnlineRoomAuthority): void {
    this.#authority = authority;
    this.#hostPeerId = undefined;
    this.#hostLoop.start();
    void this.publishLobby();
    void this.publishState();
  }

  private selectRecoveredRemoteHost(peerId: string): void {
    this.#authority = undefined;
    this.#hostLoop.stop();
    this.#hostPeerId = peerId;
  }

  private async control(
    request: ControlRequest,
    local: (authority: OnlineRoomAuthority) => unknown,
  ): Promise<void> {
    if (this.#authority) await local(this.#authority);
    else await requestOnlineControl(this.#controlAction, request, this.requiredHost());
  }

  private requiredHost(): string {
    if (!this.#hostPeerId) throw new TypeError("Room host is not connected");
    return this.#hostPeerId;
  }

  private fail(error: unknown): void {
    this.update({ phase: "error", error: onlineErrorMessage(error) });
  }

  private update(change: Partial<OnlineRoomSnapshot>): void {
    this.#snapshot = { ...this.#snapshot, ...change };
    for (const listener of this.#listeners) listener(this.#snapshot);
  }
}
